/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import { HistoricalUtrs } from '../../../../api/insights';
import { getMetricUnitDesc } from '../../../../components/utr-modal/components/chart';
import UniversalTracker from '../../../../model/UniversalTracker';
import { UniversalTrackerModalServiceUtrv } from '../../../../reducers/universal-tracker-modal';
import { getChartColors, getChartDataByValues, isAssuranceRestated } from '../../../../utils/charts';
import { getChartOptions } from '../../../summary/insights/partials/GenericChart';
import { ChartType } from '../../../summary/insights/utils/constants';
import { SingleValueWrapper } from './SingleValueWrapper';
import Chart from 'react-google-charts';
import { SurveyActionData } from '../../../../model/surveyData';
import {
  ChartSubType,
  GridDashboardChartItem,
  GridDashboardItem,
  GridDashboardSDGChartItem,
  InsightDashboardItemType,
  SubType,
} from '../../../../types/insight-custom-dashboard';
import { isSDGTrackerType } from '../../utils';
import { DATE } from '../../../../utils/date';

interface ChartWidgetProps {
  utrData?: HistoricalUtrs;
  selectedColumnCode: string | undefined;
  subType?: SubType;
  handleSetTarget?: () => void;
  readOnly?: boolean;
  unitText?: GridDashboardChartItem['unitText'] | GridDashboardSDGChartItem['unitText'];
  initiativeId: string;
  survey?: Pick<SurveyActionData, '_id' | 'fragmentUniversalTrackerValues'>;
  type: InsightDashboardItemType.Chart | InsightDashboardItemType.SDGTracker;
  altUnitText?: string;
  hideQuestionReference?: boolean;
}

export interface UniversalTrackerInsightData {
  utr: UniversalTracker | undefined;
  utrvs: UniversalTrackerModalServiceUtrv[];
}

const MIDDLE_OF_MONTH = 15;

/** @deprecated in favour of MultiUtrsChartWidget */
export const ChartWidget = (props: ChartWidgetProps) => {
  const {
    utrData,
    selectedColumnCode,
    subType = ChartSubType.Line,
    handleSetTarget,
    readOnly = false,
    unitText,
    initiativeId,
    survey,
    type,
    altUnitText,
    hideQuestionReference = false,
  } = props;

  const chartData = getChartDataByValues(utrData?.utrvs, selectedColumnCode);

  // @TODO: Need to consolidate styling of value: Refactor dashboard item configuration,
  // {
  //   color
  //   direction
  //   unitText
  // }

  const metricUnitText =
    utrData && utrData.utr ? getMetricUnitDesc(new UniversalTracker(utrData.utr), selectedColumnCode) : '';

  const isRestated = isAssuranceRestated(utrData?.utrvs);

  if (subType === ChartSubType.SingleValue || subType === ChartSubType.SparkLine) {
    const isSDGTracker = isSDGTrackerType({ type } as GridDashboardItem);
    return (
      <SingleValueWrapper
        subType={subType}
        utrData={utrData}
        chartData={chartData}
        metricUnitText={metricUnitText}
        selectedColumnCode={selectedColumnCode}
        isRestated={isRestated}
        handleSetTarget={handleSetTarget}
        readOnly={readOnly}
        unitText={unitText}
        initiativeId={initiativeId}
        survey={survey}
        type={type}
        altUnitText={altUnitText}
        haveTextsSameColor={isSDGTracker}
        hideQuestionReference={isSDGTracker || hideQuestionReference}
      />
    );
  }

  const showNoData = !((chartData || []).slice(1).length > 0);
  if (showNoData) {
    return <p className='text-sm text-medium'>No available data</p>;
  }

  // Ignore the text row, first item of second row and above will be the date
  const ticks = chartData?.slice(1).map((data) => {
    const newDate = new Date(data[0] as Date);
    // Middle of month to avoid drawing 2 ticks that are too close to each other
    newDate.setDate(MIDDLE_OF_MONTH);
    return newDate;
  });

  // Somehow using direct <Chart /> doesn't update chart's height when this component's updated.
  const RenderedChart = () => {
    return (
      <Chart
        chartType={ChartType.LineChart}
        data={chartData}
        width={'100%'}
        height={'100%'}
        options={{
          ...getChartOptions({ chartType: ChartType.LineChart, vAxisTitle: metricUnitText }),
          interpolateNulls: true,
          pointSize: 5,
          colors: getChartColors(isRestated),
          hAxis: {
            format: DATE.MONTH_YEAR_SHORT,
            ticks: ticks,
            slantedText: true,
          },
        }}
      />
    );
  };

  return (
    <div className='w-100 h-100'>
      <RenderedChart />
    </div>
  );
};
